import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { toAsset } from '../utils/url';
import api from '../services/api';

interface ThemeConfig {
  id?: number;
  name: string;
  colors: {
    primary: string;
    secondary: string;
    background: string;
    cardBackground: string;
    textPrimary: string;
    textSecondary: string;
    accent: string;
  };
  typography: {
    fontFamilyPrimary: string;
    fontFamilyHeading: string;
    fontSizeBase: string;
    fontWeightNormal: string;
    fontWeightBold: string;
  };
  layout: {
    borderRadius: string;
    shadowIntensity: string;
    animationSpeed: string;
  };
  images: {
    logoUrl?: string;
    backgroundImageUrl?: string;
    cardBackImageUrl?: string;
    faviconUrl?: string;
  };
  ctaColors?: {
    backgroundFrom: string;
    backgroundTo: string;
  };
  customCss?: string;
  // Campos de tipografia das caixas de tema
  theme_box_font_size?: string;
  theme_box_font_weight?: string;
  theme_box_font_style?: string;
  theme_box_color?: string;
  features?: {
    title?: string;
    subtitle?: string;
    items?: { title?: string; description?: string }[];
  };
  selection?: {
    title?: string;
    subtitle?: string;
  };
  tokens?: {
    title?: string;
    subtitle?: string;
    items?: { amount?: number; price?: string; description?: string }[];
  };
}

interface ThemeContextType {
  theme: ThemeConfig;
  isLoading: boolean;
  refreshTheme: () => Promise<void>;
}

const defaultTheme: ThemeConfig = {
  name: 'Tema Padrão',
  colors: {
    primary: '#8B5CF6',
    secondary: '#F59E0B',
    background: '#0F0F23',
    cardBackground: '#1A1A2E',
    textPrimary: '#FFFFFF',
    textSecondary: '#A0A0A0',
    accent: '#FF6B6B'
  },
  typography: {
    fontFamilyPrimary: "'Inter', sans-serif",
    fontFamilyHeading: "'Playfair Display', serif",
    fontSizeBase: '16px',
    fontWeightNormal: '400',
    fontWeightBold: '600'
  },
  layout: {
    borderRadius: '12px',
    shadowIntensity: 'medium',
    animationSpeed: '0.3s'
  },
  images: {
    logoUrl: undefined,
    backgroundImageUrl: undefined,
    cardBackImageUrl: undefined
  },
  customCss: ''
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);

  const loadActiveTheme = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/theme/active');
      const activeTheme = response.data;
      if (activeTheme && activeTheme.id !== 0) {
        setTheme(activeTheme);
      } else {
        setTheme(defaultTheme);
      }
    } catch (error) {
      console.error('Erro ao carregar tema:', error);
      setTheme(defaultTheme);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTheme = async () => {
    await loadActiveTheme();
  };

  // Aplicar CSS variables no documento
  useEffect(() => {
    if (!isLoading) {
      const root = document.documentElement;
      
      // Aplicar cores
      root.style.setProperty('--color-primary', theme.colors.primary);
      root.style.setProperty('--color-secondary', theme.colors.secondary);
      root.style.setProperty('--color-background', theme.colors.background);
      root.style.setProperty('--color-card-background', theme.colors.cardBackground);
      root.style.setProperty('--color-text-primary', theme.colors.textPrimary);
      root.style.setProperty('--color-text-secondary', theme.colors.textSecondary);
      root.style.setProperty('--color-accent', theme.colors.accent);
      
      // Aplicar tipografia
      root.style.setProperty('--font-family-primary', theme.typography.fontFamilyPrimary);
      root.style.setProperty('--font-family-heading', theme.typography.fontFamilyHeading);
      root.style.setProperty('--font-size-base', theme.typography.fontSizeBase);
      root.style.setProperty('--font-weight-normal', theme.typography.fontWeightNormal);
      root.style.setProperty('--font-weight-bold', theme.typography.fontWeightBold);
      
      // Aplicar tipografia das caixas de tema
      if (theme.theme_box_font_size) {
        root.style.setProperty('--theme-box-font-size', theme.theme_box_font_size);
        root.style.setProperty('--theme-box-font-weight', theme.theme_box_font_weight || 'normal');
        root.style.setProperty('--theme-box-font-style', theme.theme_box_font_style || 'normal');
        root.style.setProperty('--theme-box-color', theme.theme_box_color || '#FFFFFF');
      } else {
        // Valores padrão
        root.style.setProperty('--theme-box-font-size', '16px');
        root.style.setProperty('--theme-box-font-weight', 'normal');
        root.style.setProperty('--theme-box-font-style', 'normal');
        root.style.setProperty('--theme-box-color', '#FFFFFF');
      }
      
      // Aplicar layout
      root.style.setProperty('--border-radius', theme.layout.borderRadius);
      root.style.setProperty('--animation-speed', theme.layout.animationSpeed);
      
      // Aplicar sombras
      const shadowValue = theme.layout.shadowIntensity === 'low' 
        ? '0 2px 4px rgba(0,0,0,0.1)' 
        : theme.layout.shadowIntensity === 'medium'
        ? '0 4px 8px rgba(0,0,0,0.2)'
        : '0 8px 16px rgba(0,0,0,0.3)';
      root.style.setProperty('--shadow', shadowValue);
      
      // Aplicar imagens como CSS variables
      root.style.setProperty('--logo-url', theme.images.logoUrl ? `url(${toAsset(theme.images.logoUrl)})` : 'none');
      root.style.setProperty('--background-image-url', theme.images.backgroundImageUrl ? `url(${toAsset(theme.images.backgroundImageUrl)})` : 'none');
      root.style.setProperty('--card-back-image-url', theme.images.cardBackImageUrl ? `url(${toAsset(theme.images.cardBackImageUrl)})` : 'none');

      // Favicon: gerenciado pelo index.html (favicon.svg estático)
      // Não alteramos via JS — browsers Chromium ignoram mudanças dinâmicas no favicon
      
      // Aplicar imagem de fundo se existir
      if (theme.images.backgroundImageUrl) {
        document.body.style.backgroundImage = `url(${toAsset(theme.images.backgroundImageUrl)})`;
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundAttachment = 'fixed';
      } else {
        document.body.style.backgroundImage = '';
      }
      
      // Aplicar CSS personalizado
      let customStyleElement = document.getElementById('custom-theme-styles');
      if (customStyleElement) {
        customStyleElement.remove();
      }
      
      if (theme.customCss) {
        customStyleElement = document.createElement('style');
        customStyleElement.id = 'custom-theme-styles';
        customStyleElement.textContent = theme.customCss;
        document.head.appendChild(customStyleElement);
      }
    }
  }, [theme, isLoading]);

  // Carregar tema na inicialização
  useEffect(() => {
    loadActiveTheme();
  }, []);

  const value: ThemeContextType = {
    theme,
    isLoading,
    refreshTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;